const TermsAndConditions = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-gray-700 dark:text-gray-300 antialiased selection:bg-orange-500/10 selection:text-orange-500">
      <div className="border-b border-gray-200 dark:border-gray-800 pb-8 mb-10">  
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-2">
          Terms and Conditions
        </h1>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Last updated: <time dateTime="2026-04-20" className="font-medium text-gray-500 dark:text-gray-400">20 April 2026</time>
        </p>
      </div>
      <div className="space-y-10 text-base md:text-lg leading-relaxed">
        
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            1. Introduction
          </h2>
          <p>
            These Terms and Conditions govern your access to and use of <strong className="text-gray-900 dark:text-white font-semibold">GetEat</strong>.
            By creating an account or using our service, you explicitly agree to be bound by these terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            2. Account Registration
          </h2>
          <p className="mb-3">To access and use the service, you must register an account and provide:</p>
          <ul className="list-disc pl-5 space-y-1 mb-4 marker:text-orange-500">
            <li>First name</li>
            <li>Last name</li>
            <li>Email address</li>
          </ul>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You are entirely responsible for maintaining the confidentiality of your account credentials and ensuring that all information provided is accurate and up to date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            3. Use of the Service
          </h2>
          <p className="mb-4">You may use the platform to create, update, and manage your restaurant listings. However, you explicitly agree <span className="font-semibold text-red-500 dark:text-red-400">NOT to</span>:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm md:text-base">
            <div className="flex items-start gap-2 bg-red-50/50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/30 p-3 rounded-lg">
              <span className="text-red-500 mt-0.5">✕</span>
              <span>Provide false or misleading information</span>
            </div>
            <div className="flex items-start gap-2 bg-red-50/50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/30 p-3 rounded-lg">
              <span className="text-red-500 mt-0.5">✕</span>
              <span>Violate any applicable local or international laws</span>
            </div>
            <div className="flex items-start gap-2 bg-red-50/50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/30 p-3 rounded-lg">
              <span className="text-red-500 mt-0.5">✕</span>
              <span>Use the platform for any fraudulent or malicious purposes</span>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            4. Restaurant Data
          </h2>
          <p className="mb-3">When listing a restaurant, you may provide data including:</p>
          <ul className="list-disc pl-5 space-y-1 mb-4 marker:text-orange-500">
            <li>Physical address</li>
            <li>Phone number</li>
            <li>Geographical coordinates</li>
          </ul>
          
          <div className="bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 p-4 rounded-r-lg mb-3">
            <p className="text-amber-900 dark:text-amber-300 text-sm md:text-base font-medium">
              Please note: This information is publicly accessible, visible to all users, and indexed by search engines.
            </p>
          </div>
          <p className="text-sm text-gray-500">
            You are solely and fully responsible for any content or metadata you publish on GetEat.
          </p>
        </section>

        <section className="bg-gray-50 dark:bg-gray-800/30 rounded-xl p-5 border border-gray-100 dark:border-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            5. Limitation of Liability
          </h2>
          <p className="mb-3 text-sm md:text-base">
            GetEat provides the platform on an "as-is" basis. We are explicitly not responsible or liable for:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm md:text-base marker:text-gray-400">
            <li>The completeness, accuracy, or reliability of any restaurant information.</li>
            <li>User actions, behaviors, or interactions on or off the platform.</li>
            <li>Any direct, indirect, or incidental damages resulting from your use of the service.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            6. Account Suspension
          </h2>
          <p>
            We reserve the right, at our sole discretion, to temporarily suspend or permanently terminate your account and remove any related content without prior notice if we believe you have violated these terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            7. Changes to Terms
          </h2>
          <p>
            We may modify these Terms at any time. Your continued use of the service following any updates constitutes complete and unconditional acceptance of the revised Terms.
          </p>
        </section>

        <section className="mt-12 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              8. Questions & Support
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              If you have any questions or need clarification regarding these Terms, please reach out.
            </p>
          </div>
          <a 
            href="mailto:support@geteat.net" 
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium transition-colors border border-transparent"
          >
            support@geteat.net
          </a>
        </section>

      </div>
    </div>
  );
};

export default TermsAndConditions;